import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { Edit } from '@tempo-io/sui-icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PLUGINS } from './plugins';
import { RichTextDisplay } from './RichTextDisplay';
import { RichTextEditor } from './RichTextEditor';
import type { RichTextEditableProps } from './types';
import { useRichTextController } from './useRichTextController';

export const RichTextEditable = React.memo<RichTextEditableProps>(
  ({
    defaultValue,
    onSave,
    onCancel,
    plugins = DEFAULT_PLUGINS,
    placeholder,
    autoFocus = false,
    className,
    showEditButton = true,
  }) => {
    const { t } = useTranslation('translation');
    const controller = useRichTextController({ defaultValue, onSave, onCancel });

    if (controller.isEditing) {
      return (
        <RichTextEditor
          defaultValue={controller.value}
          onSave={controller.handleSave}
          onCancel={controller.handleCancel}
          plugins={plugins}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={className}
        />
      );
    }

    if (!controller.value.trim()) {
      return null;
    }

    return (
      <div className={cn('group relative flex items-start gap-2', className)}>
        <RichTextDisplay value={controller.value} className="flex-1" />

        {showEditButton && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={controller.startEditing}
            className="self-start opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            data-testid="rich-text-edit-button"
            aria-label={t('common.edit')}
          >
            <Edit className="text-icon-neutral-secondary" />
          </Button>
        )}
      </div>
    );
  },
);
